import React, { useEffect, useState } from 'react';
import { Formik, Form, FormikHelpers } from 'formik'
import { ApiFetch, ApiPut } from '../Common/Api'
import { Cell, Grid } from 'react-foundation'
import PageHeading from '../Common/PageHeading';
import SubmitButton, { SubmitMessage } from '../Common/SubmitButton'
import { TrackedButton } from '../Common/AnalyticsEvents'
import { CareManagerPreferencesForm } from '../../models/UserPreferences';
import { EmailNotificationPreferences } from './EmailNotificationPreferences';
import { FocusOnFirstError } from '../Common/Input/FormFields';
import { CareManagerCommunicationPreferences } from './CommunicationPreferences';
import * as Yup from 'yup';


export const defaultEmailPreferences = {
  servicePostingNotifications: {
    enabled: true,
    options: {
      perResponse: false,
      dailySummary: true
    }
  },
  bidRequestNotifications: {
    enabled: true,
    options: {
      perResponse: false,
      dailySummary: true
    }
  },
  servicePostingReminders: {
    enabled: true,
    options: {
      perPosting: false,
      dailySummary: true
    }
  },
  bidRequestReminders: {
    enabled: true,
    options: {
      perPosting: false,
      dailySummary: true
    }
  }
};

export const emailPreferencesLabels = {
  servicePostingNotifications: {
    label: 'Service Posting response notifications',
    options: {
      perResponse: 'One notification per response',
      dailySummary: 'Daily summary of responses'
    }
  },
  servicePostingReminders: {
    label: 'Service Posting expiration',
    options: {
      perPosting: 'One notification per posting',
      dailySummary: 'Daily summary of postings with no responses'
    }
  },
};

const CareManagerPreferenceForm: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<any>(null);

  const validationSchema = Yup.object().shape({
    communicationMethods: Yup.array().of(Yup.string()),
    preferredPhoneNumber: Yup.string().when('communicationMethods', {
      is: (methods: string[]) =>
        Array.isArray(methods) && methods.includes('Text Message'),
      then: schema => schema.required('Phone number is required for Text Message'),
      otherwise: schema => schema.notRequired(),
    }).matches(/^\(?\d{3}[) -.]*\d{3}[-.]?\d{4}$/, 'Invalid Phone ###-###-####'),
  });

  const mergeEmailPreferences = (beData: any): any => {
    const result: any = {};
    Object.entries(emailPreferencesLabels).forEach(([groupKey, groupLabels]) => {
      const beGroup = beData?.[groupKey] || {};
      result[groupKey] = {
        label: groupLabels.label,
        enabled: beGroup.enabled ?? true,
        options: Object.fromEntries(
          Object.entries(groupLabels.options).map(([optKey, optLabel]) => [
            optKey,
            {
              label: optLabel,
              value: beGroup.options?.[optKey] ?? false
            }
          ])
        )
      };
    });
    return { emailCommunicationPreferences: result };
  };

  const stripLabelsFromPreferences = (formValues: any): any => {
    const result: any = {};
    Object.entries(formValues).forEach(([groupKey, groupValue]: [string, any]) => {
      result[groupKey] = {
        enabled: groupValue.enabled,
        options: Object.fromEntries(
        Object.entries(groupValue.options).map(([optKey, optValue]: [string, any]) => [
          optKey,
          optValue.value
          ])
        )
      };
    });
    return result;
  }

  useEffect(() => {
    ApiFetch<CareManagerPreferencesForm>('User/CareManagerPreferences')
      .then((res) => {
        // If emailCommunicationPreferences is null/undefined, use default
        const formattedEmailPreferences = mergeEmailPreferences(
          res && res.emailCommunicationPreferences
            ? res.emailCommunicationPreferences
            : defaultEmailPreferences
        );
        setPreferences({ ...res, ...formattedEmailPreferences });
      })
      .catch(() => {
        // Only on real fetch error, use all defaults
        const prefs = mergeEmailPreferences(defaultEmailPreferences);
        setPreferences(prefs);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = (values: any, { setStatus, setSubmitting }: FormikHelpers<any>) => {
    const payload = {
      ...values,
      emailCommunicationPreferences: stripLabelsFromPreferences(values.emailCommunicationPreferences)
    };
    return ApiPut('User/CareManagerPreferences', undefined, payload)
      .then((r) => setStatus({ 'success': r }))
      .catch(({ message: error }: Error) => setStatus({ error }))
      .finally(() => setSubmitting(false));
  }

  if (loading || !preferences) return <div>Loading...</div>;

  return ( 
    <Formik
      initialValues={{
        communicationMethods:
          preferences?.communicationMethods && preferences.communicationMethods.length > 0
            ? preferences.communicationMethods
            : null,
        preferredPhoneNumber: preferences?.preferredPhoneNumber || '',
        emailCommunicationPreferences: preferences?.emailCommunicationPreferences || {}
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(form) => (
      <Grid className='preferences'>
        <Cell small={12}>
            <PageHeading pageTitle="User Preferences" createButton={false} filterButton={false} />
            <Form
              onChange={() => form.setStatus(undefined)}
            >
              <Cell className="card">
                <Cell>
                  <CareManagerCommunicationPreferences />
                </Cell>
                <Cell>
                  <EmailNotificationPreferences />
                </Cell>
                <FocusOnFirstError />
                <SubmitMessage successMessage='Saved preferences' />
                <div className='right standard-top-margin'>
                    <TrackedButton event='Update'><SubmitButton text='Save' /></TrackedButton>
                </div>
              </Cell>
          </Form>
        </Cell>
      </Grid>
      )}
  </Formik>
  );
};

export default CareManagerPreferenceForm;